import { useMutation, useQuery } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router'

import { fetchProcedure, procedureKey, type Facility } from '../api/procedures'
import { requestOtp, verifyOtp, type PatientSession } from '../api/patientAuth'
import { createServiceRequest } from '../api/serviceRequests'
import { Field } from '../components/Field'
import { Icon } from '../components/Icon'
import {
  PAGE_GUTTER_DESKTOP,
  PageBand,
  PageShell,
} from '../components/PageShell'
import { ApiError } from '../lib/api'
import { formatPesewas } from '../lib/money'

/**
 * Request a diagnostic/procedure (M11-T04) at `/service/:procedureSlug/:hospitalSlug`.
 * A service is "just a form" (ADR-0025), distinct from a bed booking: the
 * procedure + provider (and price) are fixed by the URL. The patient picks a
 * slot, verifies their phone (M10-T02, in-memory token), consents, and submits —
 * creating the request in `pending_payment`, visible to AMTC + the hospital.
 * Upfront payment (escrow) is Stage 2.
 *
 * No Phase-2 wireframe exists in `designs/` yet — reuses Phase-1 atoms/tokens.
 */
export function RequestService(): ReactNode {
  const { procedureSlug = '', hospitalSlug = '' } = useParams()
  const navigate = useNavigate()

  const query = useQuery({
    queryKey: procedureKey(procedureSlug),
    queryFn: ({ signal }) => fetchProcedure(procedureSlug, signal),
  })
  const facility: Facility | undefined = query.data?.facilities.find(
    (f) => f.hospital_slug === hospitalSlug,
  )

  const [scheduledFor, setScheduledFor] = useState('')
  const [patientName, setPatientName] = useState('')
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [codeSent, setCodeSent] = useState(false)
  const [session, setSession] = useState<PatientSession | null>(null)
  const [consent, setConsent] = useState(false)

  const requestMutation = useMutation({
    mutationFn: () => requestOtp(phone.trim()),
    onSuccess: () => setCodeSent(true),
  })
  const verifyMutation = useMutation({
    mutationFn: () => verifyOtp(phone.trim(), code.trim()),
    onSuccess: (s) => setSession(s),
  })
  const submitMutation = useMutation({
    mutationFn: async () => {
      if (session === null) throw new Error('not ready')
      return createServiceRequest(session.token, {
        hospitalSlug,
        procedureSlug,
        scheduledFor: new Date(scheduledFor).toISOString(),
        patientName: patientName.trim(),
      })
    },
  })

  const ready = scheduledFor !== '' && session !== null && consent

  return (
    <PageShell measure={594} as="main" className="font-sans text-ink-1">
      <header
        className={`flex items-center gap-2 border-b border-line-1 px-4 py-3 ${PAGE_GUTTER_DESKTOP}`}
      >
        <button
          type="button"
          aria-label="Back"
          onClick={() => navigate(-1)}
          className="rounded-full p-1 text-ink-3 hover:text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          <Icon name="chevl" size={20} />
        </button>
        <h1 className="text-base font-bold tracking-tight">
          Request a service
        </h1>
      </header>
      {/* One band for the body; nothing nested restates the measure and no
          padding moves, so mobile is unchanged by construction. */}
      <PageBand
        className="flex flex-1 flex-col"
        contentClassName="flex flex-1 flex-col"
      >
        {query.isPending ? (
          <p className="px-4 py-8 text-sm text-ink-3" role="status">
            Loading…
          </p>
        ) : query.isError || facility === undefined ? (
          <div className="px-4 py-8">
            <p role="alert" className="text-sm font-medium text-bed-bad">
              This service isn’t available at that hospital.
            </p>
            <button
              type="button"
              onClick={() => navigate('/diagnostics')}
              className="mt-3 rounded-full border border-line-2 bg-bg-app px-4 py-1.5 text-[13px] font-medium"
            >
              Back to search
            </button>
          </div>
        ) : submitMutation.isSuccess ? (
          <Confirmation
            reference={submitMutation.data.reference}
            hospitalName={facility.hospital_name}
            onDone={() => navigate('/diagnostics')}
          />
        ) : (
          <div className="flex flex-col gap-6 px-4 py-5">
            {/* Summary of what's being requested (fixed by the URL) */}
            <section className="rounded-2xl border border-line-1 bg-bg-app p-4 shadow-card">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="text-sm font-semibold">{query.data.name}</h2>
                  <p className="mt-0.5 inline-flex items-center gap-1 text-[12px] text-ink-3">
                    <Icon name="pin" size={12} /> {facility.hospital_name} ·{' '}
                    {facility.city}
                    {facility.turnaround ? (
                      <span> · {facility.turnaround}</span>
                    ) : null}
                  </p>
                </div>
                <span className="shrink-0 text-sm font-semibold tabular-nums">
                  {facility.price_pesewas != null
                    ? formatPesewas(facility.price_pesewas)
                    : '—'}
                </span>
              </div>
              {query.data.referral_required && (
                <p className="mt-2 inline-flex items-center gap-1 rounded-full bg-accent-bg px-2 py-0.5 text-[11px] font-medium text-accent-ink">
                  <Icon name="info" size={11} /> Referral needed — bring it to
                  your appointment
                </p>
              )}
            </section>

            {/* 1 — when */}
            <section aria-labelledby="when-h">
              <StepHeading id="when-h" n={1}>
                Choose a date &amp; time
              </StepHeading>
              {/* M17-T11 — the shared Field from T09. The visible label gives
                the same accessible name the aria-label did, so existing
                assertions still find it. */}
              <Field
                className="mt-2"
                label="Appointment date and time"
                type="datetime-local"
                value={scheduledFor}
                onChange={(e) => setScheduledFor(e.target.value)}
              />
            </section>

            {/* 2 — your details + phone verify */}
            <section aria-labelledby="who-h">
              <StepHeading id="who-h" n={2}>
                Your details
              </StepHeading>
              <Field
                className="mt-2"
                label="Your name"
                type="text"
                placeholder="Your name (optional)"
                value={patientName}
                onChange={(e) => setPatientName(e.target.value)}
              />
              {session ? (
                <p className="mt-2 inline-flex items-center gap-1 rounded-full bg-bed-good-bg px-2.5 py-1 text-[13px] font-medium text-bed-good">
                  <Icon name="check" size={14} /> Verified · {phone.trim()}
                </p>
              ) : (
                <div className="mt-2 flex flex-col gap-2">
                  <Field
                    label="Phone number"
                    type="tel"
                    placeholder="Phone, e.g. +233…"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    trailing={
                      <button
                        type="button"
                        disabled={
                          phone.trim().length < 8 || requestMutation.isPending
                        }
                        onClick={() => requestMutation.mutate()}
                        className="shrink-0 rounded-r3 border border-accent bg-accent-bg px-3 text-[13px] font-medium text-accent-ink disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                      >
                        {requestMutation.isPending
                          ? 'Sending…'
                          : codeSent
                            ? 'Resend'
                            : 'Send code'}
                      </button>
                    }
                  />
                  {requestMutation.isError && (
                    <p role="alert" className="text-[12px] text-bed-bad">
                      {errMsg(requestMutation.error)}
                    </p>
                  )}
                  {codeSent && (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        inputMode="numeric"
                        aria-label="Verification code"
                        placeholder="6-digit code"
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        className="h-11 w-full rounded-r2 border border-line-2 bg-bg-app px-3 text-sm text-ink-1 placeholder:text-ink-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                      />
                      <button
                        type="button"
                        disabled={
                          code.trim().length < 4 || verifyMutation.isPending
                        }
                        onClick={() => verifyMutation.mutate()}
                        className="shrink-0 rounded-r2 bg-accent px-3 text-[13px] font-medium text-accent-on disabled:opacity-50"
                      >
                        {verifyMutation.isPending ? 'Verifying…' : 'Verify'}
                      </button>
                    </div>
                  )}
                  {verifyMutation.isError && (
                    <p role="alert" className="text-[12px] text-bed-bad">
                      {errMsg(verifyMutation.error)}
                    </p>
                  )}
                </div>
              )}
            </section>

            {/* 3 — consent + submit */}
            <section aria-labelledby="confirm-h">
              <StepHeading id="confirm-h" n={3}>
                Confirm
              </StepHeading>
              <label className="mt-2 flex items-start gap-2 text-[13px] text-ink-2">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-accent"
                />
                <span>
                  I understand this sends my request to {facility.hospital_name}{' '}
                  and the AMTC team, who will confirm the date and details with
                  me.
                </span>
              </label>

              <button
                type="button"
                disabled={!ready || submitMutation.isPending}
                aria-disabled={!ready || submitMutation.isPending}
                onClick={() => submitMutation.mutate()}
                className={`mt-4 w-full rounded-xl bg-accent px-4 py-3.5 text-sm font-semibold text-accent-on shadow-sm transition-[transform,box-shadow,opacity] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent ${
                  !ready || submitMutation.isPending
                    ? 'cursor-not-allowed opacity-50'
                    : 'hover:-translate-y-0.5 hover:shadow-lift active:translate-y-0'
                }`}
              >
                {submitMutation.isPending
                  ? 'Sending request…'
                  : 'Request this service'}
              </button>
              {submitMutation.isError ? (
                <p
                  role="alert"
                  className="mt-1.5 text-center text-[12px] text-bed-bad"
                >
                  {submitErrMsg(submitMutation.error)}
                </p>
              ) : (
                <p className="mt-1.5 text-center text-[12px] text-ink-3">
                  {ready
                    ? 'No payment now — this sends your request to AMTC and the hospital.'
                    : 'Pick a time, verify your phone, and consent to continue.'}
                </p>
              )}
            </section>
          </div>
        )}
      </PageBand>
    </PageShell>
  )
}

function Confirmation({
  reference,
  hospitalName,
  onDone,
}: {
  reference: string
  hospitalName: string
  onDone: () => void
}): ReactNode {
  return (
    <div className="flex flex-col items-center gap-4 px-6 py-12 text-center">
      <span className="animate-pop-in flex h-16 w-16 items-center justify-center rounded-full bg-bed-good-bg text-bed-good ring-4 ring-bed-good/10">
        <Icon name="check" size={30} />
      </span>
      <h2 className="text-xl font-bold tracking-tight">Request sent</h2>
      <p className="max-w-sm text-sm text-ink-2">
        {hospitalName} and the AMTC team can now see your request. We’ll be in
        touch to confirm your appointment.
      </p>
      <p className="rounded-full bg-bg-soft px-3 py-1 text-[12px] text-ink-3 ring-1 ring-inset ring-line-1">
        Reference: <span className="font-medium text-ink-2">{reference}</span>
      </p>
      <button
        type="button"
        onClick={onDone}
        className="mt-2 rounded-xl bg-accent px-5 py-2.5 text-sm font-semibold text-accent-on shadow-sm transition-[transform,box-shadow] hover:-translate-y-0.5 hover:shadow-lift focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
      >
        Find another test
      </button>
    </div>
  )
}

function StepHeading({
  id,
  n,
  children,
}: {
  id: string
  n: number
  children: ReactNode
}): ReactNode {
  return (
    <h2
      id={id}
      className="flex items-center gap-2 text-sm font-semibold text-ink-1"
    >
      <span
        className="grid h-6 w-6 place-items-center rounded-full bg-accent-bg text-[12px] font-bold text-accent-ink ring-1 ring-inset ring-accent/20"
        aria-hidden="true"
      >
        {n}
      </span>
      {children}
    </h2>
  )
}

function errMsg(err: unknown): string {
  return err instanceof ApiError
    ? err.message
    : 'Something went wrong. Try again.'
}

function submitErrMsg(err: unknown): string {
  if (err instanceof ApiError) {
    switch (err.code) {
      case 'invalid_slot':
        return 'Please pick a time in the future.'
      case 'not_bookable':
        return 'That service isn’t bookable here right now.'
      default:
        return err.message
    }
  }
  return 'Could not send your request. Please try again.'
}
